import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'

const QUERY_KEY = 'SettingsAppointmentTypeBookingWindows'
const EMPTY_OBJECT = {}

const queryKeyFor = appointmentTypeId => [QUERY_KEY, appointmentTypeId]

const urlFor = appointmentTypeId =>
  `${baseURL}/v2/rest/settings/appointment_types/${appointmentTypeId}/booking_windows`

// The update endpoint answers 422 with the errors of every window that was sent,
// grouped by day. The sibling settings hooks collapse a failure to its message,
// which would throw that map away, so it is carried on the error instead. Its
// field keys are snake_case: the success payload goes through jbuilder and comes
// back camelCase, the error payload does not.
export class BookingWindowsError extends Error {
  constructor(message, { status, errors } = {}) {
    super(message)
    this.name = 'BookingWindowsError'
    this.status = status
    this.errors = errors
  }

  // Only a 422 carries the per-window map. Every other failure answers with a
  // string, which says nothing about an individual field.
  get errorsByDay() {
    return this.status === 422 && typeof this.errors === 'object'
      ? this.errors
      : null
  }
}

export const useSettingsAppointmentTypeBookingWindows = appointmentTypeId => {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeyFor(appointmentTypeId),
    queryFn: async () => {
      const res = await fetch(urlFor(appointmentTypeId), {
        method: 'GET',
        headers,
      })
      if (!res.ok) {
        throw new Error("We couldn't load the booking rules")
      }
      return res.json()
    },
    enabled: !!appointmentTypeId,
  })

  return { bookingWindows: data ?? EMPTY_OBJECT, isLoading, isError }
}

export const useUpdateAppointmentTypeBookingWindows = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateBookingWindows, ...rest } = useMutation({
    mutationFn: async ({ appointmentTypeId, bookingWindows }) => {
      const res = await fetch(urlFor(appointmentTypeId), {
        method: 'PUT',
        headers,
        body: JSON.stringify({ bookingWindows }),
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new BookingWindowsError(
          typeof body.errors === 'string'
            ? body.errors
            : 'Could not update the booking rules',
          { status: res.status, errors: body.errors }
        )
      }

      return res.json()
    },
    // The list of appointment types carries each rule as a summary, so it goes
    // stale along with the week it was drawn from.
    onSuccess: (_data, { appointmentTypeId }) => {
      queryClient.invalidateQueries(queryKeyFor(appointmentTypeId))
      queryClient.invalidateQueries(['SettingsAppointmentTypes'])
    },
  })

  return { updateBookingWindows, ...rest }
}
